import React, { useState } from 'react';
import { Empty, ErrorText, Panel, useChrome } from './ceoChrome';

export default function FeatureFlagsPanel({ colors, narrow, data, error, onAction, busy }) {
  const chrome = useChrome(colors, narrow);
  const flags = (data && data.flags) || [];
  const agents = (data && data.agents) || [];
  const [draft, setDraft] = useState({ key: '', description: '', state: 'off', betaProfileIds: [] });
  const [pending, setPending] = useState(false);
  const [localError, setLocalError] = useState('');

  function toggleBeta(id) {
    setDraft((current) => {
      const has = current.betaProfileIds.includes(id);
      return {
        ...current,
        betaProfileIds: has ? current.betaProfileIds.filter((item) => item !== id) : current.betaProfileIds.concat(id),
      };
    });
    setPending(false);
  }

  async function save(next) {
    if (!onAction) return;
    setLocalError('');
    try {
      await onAction({ action: 'save', confirm: true, ...next });
      setPending(false);
      setDraft({ key: '', description: '', state: 'off', betaProfileIds: [] });
    } catch (err) {
      setLocalError(err.message || 'Could not save the flag');
    }
  }

  return (
    <div style={chrome.page} data-testid="ceo-flags">
      <div>
        <h2 style={chrome.title}>Feature switches</h2>
        <p style={chrome.sub}>Off hides a feature. On shows it to everyone. Beta shows it to the agents you pick. The app reads useFeatureFlag.</p>
      </div>
      {error || localError ? <ErrorText>{error || localError}</ErrorText> : null}
      {!data && !error ? <Empty chrome={chrome}>Loading…</Empty> : null}
      {flags.map((flag) => (
        <Panel key={flag.key} chrome={chrome} title={flag.key}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{flag.key}</div>
              <div style={{ color: chrome.secondary, marginTop: 4 }}>{flag.description || 'No description'}</div>
            </div>
            <div style={{ fontWeight: 600 }}>{flag.state}</div>
          </div>
          {flag.state === 'beta' ? (
            <div style={{ color: chrome.secondary, marginTop: 8 }}>{(flag.beta_profile_ids || []).length} agents in beta</div>
          ) : null}
        </Panel>
      ))}
      <Panel chrome={chrome} title="Save a flag">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input aria-label="Flag key" value={draft.key} onChange={(event) => setDraft({ ...draft, key: event.target.value })} placeholder="flag_key" style={chrome.field} />
          <input aria-label="Description" value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} placeholder="Description" style={chrome.field} />
          <select aria-label="State" value={draft.state} onChange={(event) => setDraft({ ...draft, state: event.target.value })} style={chrome.field}>
            <option value="off">Off</option>
            <option value="on">On</option>
            <option value="beta">Beta</option>
          </select>
          {draft.state === 'beta' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflow: 'auto' }}>
              {agents.map((agent) => (
                <label key={agent.id} style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44 }}>
                  <input type="checkbox" checked={draft.betaProfileIds.includes(agent.id)} onChange={() => toggleBeta(agent.id)} />
                  <span>{agent.name || agent.email}</span>
                </label>
              ))}
            </div>
          ) : null}
          {pending ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" style={chrome.button} disabled={busy} onClick={() => save(draft)}>Save</button>
              <button type="button" style={chrome.quiet} onClick={() => setPending(false)}>Cancel</button>
            </div>
          ) : (
            <button type="button" style={chrome.button} disabled={!draft.key.trim()} onClick={() => setPending(true)}>Save flag</button>
          )}
        </div>
      </Panel>
    </div>
  );
}
